import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../lib/firebase';
import { Image, Upload } from 'lucide-react';
import { useTranslation } from '../i18n/useTranslation';

interface ImageUploadProps {
  onUploadComplete: (url: string) => void;
  className?: string;
}

export default function ImageUpload({ onUploadComplete, className = '' }: ImageUploadProps) {
  const { t } = useTranslation();
  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    try {
      const file = acceptedFiles[0];
      if (!file) return;

      // Create a storage reference
      const storageRef = ref(storage, `logos/${Date.now()}-${file.name}`);
      
      // Upload the file
      await uploadBytes(storageRef, file);
      
      // Get the download URL
      const url = await getDownloadURL(storageRef);
      
      onUploadComplete(url);
    } catch (error) {
      console.error('Error uploading file:', error);
    }
  }, [onUploadComplete]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif']
    },
    maxFiles: 1,
    maxSize: 5 * 1024 * 1024 // 5MB
  });

  return (
    <div
      {...getRootProps()}
      className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
        isDragActive 
          ? 'border-[#1b3968] bg-[#1b3968]/5' 
          : 'border-gray-300 hover:border-[#1b3968] hover:bg-[#1b3968]/5'
      } ${className}`}
    >
      <input {...getInputProps()} />
      <div className="flex flex-col items-center gap-2">
        {isDragActive ? (
          <Upload className="h-8 w-8 text-[#1b3968]" />
        ) : (
          <Image className="h-8 w-8 text-gray-400" />
        )}
        <p className="text-sm text-gray-600">
          {isDragActive
            ? t('notifications.dropImageHere')
            : t('notifications.dragDropImage')}
        </p>
        <p className="text-xs text-gray-500">
          {t('notifications.maxFileSize5MB')}
        </p>
      </div>
    </div>
  );
}